/**
 * Cash Management API Client
 * Story 62-12: Cash Management Frontend UI
 */

import { api, ApiError } from './client';

// ============================================
// TYPES
// ============================================

export interface CashRegister {
  id: string;
  tenantId: string;
  date: string;
  status: string;
  openedBy: string;
  openedAt: string;
}

export interface CashPocket {
  type: 'REGISTER' | 'DEPOSIT' | 'CREDIT';
  openingBalance: number;
  totalIn: number;
  totalOut: number;
  currentBalance: number;
  transactionCount: number;
}

export interface DailySummary {
  date: string;
  status: string;
  totalBalance: number;
  pockets: CashPocket[];
  transactions: MaskedTransaction[];
}

export interface MaskedTransaction {
  id: string;
  amount: number;
  direction: string;
  category: string;
  note?: string;
  createdAt: string;
  isMasked: boolean;
}

export interface BlindCloseResult {
  newStatus: string;
  discrepancy: number;
  withinTolerance: boolean;
  requiresRecount: boolean;
  requiresProtocol: boolean;
  /** P-CSH-1: Per-pocket expected vs physical breakdown */
  pocketDetails?: Array<{
    type: string;
    expected: number;
    physical: number;
    discrepancy: number;
  }>;
  /** P-CSH-1: true when difference auto-accepted from SZH pocket */
  autoAccepted?: boolean;
}

/** P-CSH-1: Close summary with expected amounts (nyílt zárás) */
export interface CloseSummaryResult {
  pockets: Array<{
    type: string;
    expectedBalance: number;
  }>;
  totalExpected: number;
  tolerance: number;
}

export interface SupplierCredit {
  id: string;
  szhId?: string;
  partnerId?: string;
  partnerName?: string;
  type: string;
  status: string;
  amount: number;
  description: string;
  isAnonymous: boolean;
  createdAt: string;
}

export interface SzhSummary {
  expectedRevenue: number;
  expectedSettlement: number;
  totalBalance: number;
  openItems: number;
}

export interface ReserveBalance {
  reserveCredits: number;
  reserveDebits: number;
  netReserve: number;
  itemCount: number;
}

export interface SzhDailyReport {
  reportId: string;
  date: string;
  creditItems: unknown[];
  accountableExpenseItems: unknown[];
  reserveItems: unknown[];
  totals: {
    creditTotal: number;
    accountableExpenseTotal: number;
    reserveTotal: number;
    grandTotal: number;
  };
}

// ============================================
// API CALLS
// ============================================

export async function openCashRegister(carryOverBalances?: boolean) {
  return api.post<{ data: { register: CashRegister; pockets: CashPocket[] } }>('/cash/registers', {
    carryOverBalances,
  });
}

export async function getTodayRegister() {
  return api.get<{ data: CashRegister }>('/cash/registers/today');
}

export async function getDailySummary(registerId: string) {
  return api.get<{ data: DailySummary }>(`/cash/registers/${registerId}/summary`);
}

export async function recordTransaction(
  registerId: string,
  data: {
    pocketType: 'REGISTER' | 'DEPOSIT' | 'CREDIT';
    amount: number;
    direction: 'IN' | 'OUT';
    category?: string;
    note?: string;
    expenseCategory?: string;
  }
) {
  return api.post<{ data: unknown }>(`/cash/registers/${registerId}/transactions`, data);
}

// SALES-FG-4-2: Expense summary by category
export interface ExpenseSummaryCategory {
  category: string;
  count: number;
  total: number;
}

export interface ExpenseSummary {
  dateFrom: string;
  dateTo: string;
  categories: ExpenseSummaryCategory[];
  grandTotal: number;
  totalCount: number;
}

export async function getExpenseSummary(dateFrom?: string, dateTo?: string) {
  const params = new URLSearchParams();
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  const query = params.toString() ? `?${params.toString()}` : '';
  return api.get<{ data: ExpenseSummary }>(`/cash/expense-summary${query}`);
}

// Daily Close (P-CSH-1: nyílt zárás)
export async function getCloseSummary(registerId: string) {
  return api.get<{ data: CloseSummaryResult }>(`/cash/registers/${registerId}/close/summary`);
}

export async function startBlindClose(registerId: string) {
  return api.post<{ data: { previousStatus: string; newStatus: string } }>(
    `/cash/registers/${registerId}/close/start`
  );
}

export async function submitCount(
  registerId: string,
  pocketCounts: Array<{ pocketType: string; physicalAmount: number }>
) {
  return api.post<{ data: BlindCloseResult }>(`/cash/registers/${registerId}/close/submit`, {
    pocketCounts,
  });
}

export async function finalizeClose(
  registerId: string,
  pocketCounts: Array<{ pocketType: string; physicalAmount: number }>
) {
  return api.post<{ data: unknown }>(`/cash/registers/${registerId}/close/finalize`, {
    pocketCounts,
  });
}

// SZH
export async function listSzhItems(registerId: string, status?: string) {
  const query = status ? `?status=${status}` : '';
  return api.get<{ data: SupplierCredit[]; total: number }>(
    `/cash/registers/${registerId}/szh${query}`
  );
}

export async function getSzhSummary(registerId: string) {
  return api.get<{ data: SzhSummary }>(`/cash/registers/${registerId}/szh/summary`);
}

export async function issueCredit(
  registerId: string,
  data: { partnerId?: string; partnerName?: string; amount: number; activity: string }
) {
  return api.post<{ data: SupplierCredit }>(`/cash/registers/${registerId}/szh`, data);
}

export async function issueAnonymousCredit(
  registerId: string,
  data: { amount: number; activity: string }
) {
  return api.post<{ data: SupplierCredit }>(`/cash/registers/${registerId}/szh/anonymous`, data);
}

export async function settleCredit(creditId: string) {
  return api.patch<{ data: unknown }>(`/cash/szh/${creditId}/settle`, {});
}

export async function cancelCredit(creditId: string) {
  return api.patch<{ data: unknown }>(`/cash/szh/${creditId}/cancel`, {});
}

export async function getReserveBalance(registerId: string) {
  return api.get<{ data: ReserveBalance }>(`/cash/registers/${registerId}/szh/reserve`);
}

export async function generateSzhReport(registerId: string, notes?: string) {
  return api.post<{ data: SzhDailyReport }>(`/cash/registers/${registerId}/szh/report`, { notes });
}

// ============================================
// EXPENSE VOUCHERS (Story 62-8, 62-9)
// ============================================

export interface ExpenseVoucher {
  id: string;
  tenantId: string;
  voucherNumber: string;
  recipientId: string;
  recipientName: string;
  amount: number;
  purpose: string;
  status: 'ISSUED' | 'SETTLED' | 'OVERDUE' | 'CANCELLED';
  settledAmount: number | null;
  settledAt: string | null;
  returnedAmount: number | null;
  receiptAttached: boolean;
  issuedAt: string;
  deadline: string;
  isOverdue: boolean;
  issuedBy: string;
  createdAt: string;
}

export async function listExpenseVouchers(status?: string) {
  const query = status ? `?status=${status}` : '';
  return api.get<{ data: ExpenseVoucher[]; total: number }>(`/cash/expense-vouchers${query}`);
}

export async function issueExpenseVoucher(data: {
  recipientId: string;
  recipientName: string;
  amount: number;
  purpose: string;
}) {
  return api.post<{ data: ExpenseVoucher }>('/cash/expense-vouchers', data);
}

export async function getExpenseVoucher(id: string) {
  return api.get<{ data: ExpenseVoucher }>(`/cash/expense-vouchers/${id}`);
}

export async function settleExpenseVoucher(
  id: string,
  data: { settledAmount: number; returnedAmount?: number; receiptAttached?: boolean }
) {
  return api.post<{ data: ExpenseVoucher }>(`/cash/expense-vouchers/${id}/settle`, data);
}

export async function cancelExpenseVoucher(id: string) {
  return api.post<{ data: ExpenseVoucher }>(`/cash/expense-vouchers/${id}/cancel`);
}

// ============================================
// Tranzakció-rögzítés hiba-osztályozás (67-kör 1️⃣)
// ============================================

/**
 * A kassza tranzakció-rögzítés hibájának pultos-szintű osztályozása.
 *
 * Reuse-minta: `deposits.ts` depositActionErrorToastKey — ApiError → (1) strukturált
 * error.code → (2) HTTP-status-fallback → (3) nem-ApiError. Az eltérés: itt a hívó
 * a státusz SZERINT MÁS UI-t rendel (409 = másik fél + kiút, 422 = mezőhöz kötve),
 * ezért nem toast-kulcsot adunk vissza, hanem osztályt + a fókuszálandó mezőt.
 *
 * ⚠️ Optional-guard: ismeretlen státusz/kód esetén a 'failed' ág fut ⇒ a FE nem
 * függ a BE-oldali státuszkód-váltás (PR-D) merge-sorrendjétől.
 */
export type RecordTxErrorKind = 'conflict' | 'validation' | 'failed';

export interface RecordTxError {
  kind: RecordTxErrorKind;
  /** 422-nél a fókuszálandó mező, ha a BE megnevezte; egyébként undefined. */
  field?: 'amount' | 'note';
}

export function classifyRecordTxError(error: unknown): RecordTxError {
  if (!(error instanceof ApiError)) return { kind: 'failed' };

  // (1) strukturált error.code — a mező-kötés CSAK innen jöhet
  const code = error.code ?? '';
  if (code.includes('AMOUNT')) return { kind: 'validation', field: 'amount' };
  if (code.includes('NOTE')) return { kind: 'validation', field: 'note' };

  // (2) HTTP-status-fallback
  switch (error.statusCode) {
    case 409:
      return { kind: 'conflict' };
    case 422:
    case 400:
      return { kind: 'validation' };
    default:
      return { kind: 'failed' };
  }
}
